import React, { useCallback, useState } from "react";
import { Field, FieldRenderProps, Form, FormSpy } from "react-final-form";
import { TimeInput } from "component/inputs/TimeInput";
import "./general.scss";
import { Button } from "@pickit/pickit-components";
import { PolygonGeneralForm, PolygonGeneralProps } from "./types";
import useValidationSchema from "hooks/useValidationSchema";
import { WorkzonesTable } from "../WorkzonesTable/WorkzonesTable";
import WorkzoneModal from "../WorkzoneModal/WorkzoneModalContainer";
import RainBonusModal from "../rainBonusModal/RainBonusModalContainer";
import { BasicIcon, NumericInput } from "@pickit/pickit-components";
import "./i18n";
import { useTranslation } from "react-i18next";
import { formatDDMMYYYY, formatHHMM, isISOTimeAfterNow } from "utils/date";

export const General = (props: PolygonGeneralProps) => {
  const { t } = useTranslation();
  const { testId } = props;

  const renderKilometerInput = useCallback(
    (props: FieldRenderProps<HTMLElement>, testId: string) => {
      return (
        <>
          <NumericInput label={t("polygonGeneral:kilometers")} showClearIcon helperText="La unidad de medida es en kilómetros" {...props} testId={testId} />
        </>
      );
    },
    [t]
  );

  const [enableMaxDistance, setEnableMaxDistance] = useState(false);

  return (
    <>
      <Form
        onSubmit={(values) => props.onSubmit(values as PolygonGeneralForm)}
        initialValues={props.initialValues}
        validate={useValidationSchema(props.validationSchema)}
        mutators={{
          setValue: ([field], state) => {
            if (field === "deliveryOperationTime.initial") {
              const f = state.fields["deliveryOperationTime.end"];
              f.touched = true;
            }
          },
        }}
      >
        {({ handleSubmit, form, dirty, invalid }) => {
          props.formRef.current = form as any;
          return (
            <form onSubmit={handleSubmit}>
              <FormSpy
                subscription={{
                  dirty: true,
                  values: true,
                  invalid: true,
                }}
                onChange={(form) => {
                  props.setDirty(form.dirty);
                  setEnableMaxDistance(form.values.trip?.enable);
                }}
              />
              <div className="container-polygon-general">
                <div className="container-polygon-general-row">
                  <div className="container-polygon-general-col-sm-6 container-polygon-general-col-xl-4">
                    <h2 data-testId={`${testId}-distances-title`}>
                      {t("polygonGeneral:distances")}
                    </h2>
                    <div className="polygon-form-card">
                      <div className="numeric-field-container">
                        <Field
                          component={NumericInput}
                          name="distance.maxDistanceForBoundary"
                          label={t("polygonGeneral:maxForBoundary")}
                          className="Admin-Pickers-input"
                          language="es"
                          maxInt={3}
                          allowDecimal={false}
                          disabled={props.disableForm}
                          placeholder={t("polygonGeneral:enterDistance")}
                        >
                          {(props) =>
                            renderKilometerInput(
                              props,
                              `${testId}-max-distance-boundary`
                            )
                          }
                        </Field>
                      </div>
                      <div className="numeric-field-container">
                        <Field
                          name="distance.maxBetweenPoints"
                          label={t("polygonGeneral:maxBetweenPoints")}
                          className="Admin-Pickers-input"
                          language="es"
                          maxInt={3}
                          allowDecimal={false}
                          disabled={props.disableForm}
                          placeholder={t("polygonGeneral:enterDistance")}
                        >
                          {(props) =>
                            renderKilometerInput(
                              props,
                              `${testId}-max-distance-between-points`
                            )
                          }
                        </Field>
                      </div>
                      <div className="numeric-field-container">
                        <Field
                          name="trip.maximumTravelDistance"
                          label={t("polygonGeneral:tripMaxDistance")}
                          className="Admin-Pickers-input"
                          language="es"
                          maxInt={3}
                          allowDecimal={false}
                          disabled={props.disableForm || !enableMaxDistance}
                          placeholder={
                            enableMaxDistance
                              ? t("polygonGeneral:enterDistance")
                              : "-"
                          }
                        >
                          {(props) =>
                            renderKilometerInput(
                              props,
                              `${testId}-max-travel-distance`
                            )
                          }
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div className="container-polygon-general-col-sm-6 container-polygon-general-col-xl-4">
                    <h2 data-testId={`${testId}-schedule-title`}>
                      {t("polygonGeneral:schedule")}
                    </h2>
                    <div className="polygon-form-card">
                      <Field
                        type="text"
                        name="deliveryOperationTime.initial"
                        label={t("polygonGeneral:startOfOperationTime")}
                        component={TimeInput}
                        className="Admin-Pickers-input"
                        language="es"
                        placeholder={t("polygonGeneral:enterTime")}
                        disabled={props.disableForm}
                        onChange={form.mutators.setValue}
                        testId={`${testId}-operation-time-start`}
                        showClearIcon
                      />
                      <Field
                        type="text"
                        name="deliveryOperationTime.end"
                        label={t("polygonGeneral:endOfOperationTime")}
                        component={TimeInput}
                        className="Admin-Pickers-input"
                        language="es"
                        placeholder={t("polygonGeneral:enterTime")}
                        disabled={props.disableForm}
                        testId={`${testId}-operation-time-end`}
                        showClearIcon
                      />
                      <div className="polygon-general-container">
                        <h2
                          className="rain-bonus"
                          data-testId={`${testId}-rain-bonus-title`}
                        >
                          {t("polygonGeneral:rainBonus")}
                        </h2>
                        <button
                          className={`detail-container ${
                            props.disableForm ? "disabled-rain" : ""
                          }`}
                          onClick={() => props.toggleRainModalVisibility(true)}
                          type="button"
                          disabled={props.disableForm}
                          data-testId={`${testId}-rain-bonus-button`}
                        >
                          <BasicIcon
                            color={props.disableForm ? "#CCC" : "#0098ff"}
                            name={"box"}
                          />
                          <p data-testId={`${testId}-rain-bonus-button-label`}>
                            {t("polygonGeneral:detail")}
                          </p>
                        </button>
                      </div>
                      <p
                        className="next-on-progress"
                        data-testId={`${testId}-next-rain`}
                      >
                        {props.nextRain?.startAt
                          ? `${
                              isISOTimeAfterNow(props.nextRain?.startAt)
                                ? t("polygonGeneral:next")
                                : t("polygonGeneral:inProgress")
                            } ${formatDDMMYYYY(
                              props.nextRain?.startAt || ""
                            )} ${formatHHMM(
                              props.nextRain?.startAt || ""
                            )} - ${formatHHMM(props.nextRain?.finishAt || "")}`
                          : `${t("polygonGeneral:next")} ${t(
                              "rainBonus:noNextRain"
                            )}`}
                      </p>
                    </div>
                  </div>
                  <div className="container-polygon-general-col-sm-12 container-polygon-general-col-xl-4">
                    <div className="container-polygon-general-row">
                      <div className="container-polygon-general-col-sm-6 container-polygon-general-col-xl-12">
                        <div className="field-container trip">
                          <h2 data-testId={`${testId}-trip-title`}>
                            {t("polygonGeneral:trip")}
                          </h2>
                        </div>
                        <div className="polygon-form-card">
                          <div className="numeric-field-container">
                            <Field
                              name="pickUpMaxCapacity"
                              label={t("polygonGeneral:pickUpMaxCapacity")}
                              component={NumericInput}
                              className="Admin-Pickers-input"
                              language="es"
                              maxInt={2}
                              allowDecimal={false}
                              disabled={props.disableForm}
                              placeholder={
                                !props.disableForm
                                  ? t("polygonGeneral:enterCapacity")
                                  : "-"
                              }
                              testId={`${testId}-max-pick-up`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="container-polygon-general-row">
                  <div className="container-polygon-general-col-sm-12 container-polygon-general-col-xl-12">
                    <div className="polygon-general-container">
                      <h2 data-testId={`${testId}-transactions-title`}>
                        {t("polygonGeneral:transactions")}
                      </h2>
                      <button
                        type="button"
                        disabled={props.disableForm}
                        className="button-workzones"
                        onClick={() => props.editWorkzones(dirty, form.reset)}
                        data-testId={`${testId}-button-change-quantity`}
                      >
                        <BasicIcon
                          color={props.disableForm ? "#CCC" : "#0098ff"}
                          name={"pencil"}
                        />
                        <p
                          data-testId={`${testId}-button-change-quantity-label`}
                        >
                          {t("polygonGeneral:changeQuantity")}
                        </p>
                      </button>
                    </div>
                    <WorkzonesTable
                      timezone={
                        isNaN(Number(props.region?.config?.timeZone))
                          ? 0
                          : Number(props.region?.config?.timeZone)
                      }
                      workzones={props.region?.workZones || []}
                      testId={`${testId}-workzones-table`}
                    />
                  </div>
                </div>
                <div className="container-polygon-general-buttons top-40">
                  <Button
                    width="220"
                    hierarchy="secondary"
                    disabled={!props.region || !dirty || props.filters?.updated}
                    onClick={() => props.restore(dirty, form)}
                    testId={`${testId}-button-restore`}
                    label={t("global:restore")}
                  />
                  <Button
                    width="220"
                    hierarchy="primary"
                    type="submit"
                    disabled={
                      !props.region ||
                      !dirty ||
                      invalid ||
                      props.filters?.updated
                    }
                    testId={`${testId}-button-save`}
                    label={t("global:saveChanges")}
                  />
                </div>
              </div>
            </form>
          );
        }}
      </Form>
      <WorkzoneModal
        isOpen={props.showModal}
        onClose={() => props.toggleModalVisibility(false)}
        testId={`${testId}-workzone-modal`}
      />
      <RainBonusModal
        isOpen={props.showRainModal}
        onClose={() => props.toggleRainModalVisibility(false)}
        testId={`${testId}-rain-modal`}
      />
    </>
  );
};
